// src/client/screens/borrower/ProfileScreen.tsx
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Pencil, Check, X, Lock, ChevronRight,
  LogOut, Trash2, AlertTriangle, CheckCircle2, Clock, XCircle, Shield,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@client/auth/useAuth';
import { useToast } from '@client/components/ToastProvider';
import { useNotificationPrefs } from '@client/hooks/useNotificationPrefs';
import { useAvatarUpload } from '@client/hooks/useAvatarUpload';
import AppHeader from '@client/components/AppHeader';
import Button from '@client/components/Button';
import NavBar from '@client/components/NavBar';
import { formatZambiaPhone } from '@client/utils/validation';
import { supabase } from '@client/lib/supabase';
import type { KycStatus } from '@client/lib/fixtures';

const APP_VERSION = '1.0.0';

// ── KYC config ──────────────────────────────────────────────────────────────
interface KycConfig {
  label: string;
  color: string;
  bg: string;
  description: string;
  Icon: React.ElementType;
}
const KYC_CONFIG: Record<KycStatus, KycConfig> = {
  verified: {
    label: 'Verified', color: '#16A34A', bg: '#ECFDF5',
    description: 'Your identity is confirmed',
    Icon: CheckCircle2,
  },
  pending: {
    label: 'Pending', color: '#D97706', bg: '#FFFBEB',
    description: "We're reviewing your documents",
    Icon: Clock,
  },
  manual_review: {
    label: 'Under review', color: '#D97706', bg: '#FFFBEB',
    description: 'An agent is reviewing your NRC photos',
    Icon: Clock,
  },
  rejected: {
    label: 'Rejected', color: '#DC2626', bg: '#FEF2F2',
    description: 'Your documents could not be verified',
    Icon: XCircle,
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatMemberSince(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: 44,
        height: 26,
        borderRadius: 13,
        border: 'none',
        background: checked ? 'var(--c-primary-700)' : 'var(--c-border)',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: checked ? 21 : 3,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.2s',
      }} />
    </button>
  );
}

// ── Row helpers ───────────────────────────────────────────────────────────────
function InfoRow({ label, value, action }: { label: string; value: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</p>
        <div style={{ margin: '2px 0 0', fontSize: 15, color: 'var(--c-text)', fontWeight: 500 }}>{value}</div>
      </div>
      {action}
    </div>
  );
}

function SupportRow({ icon: Icon, label, href }: { icon: React.ElementType; label: string; href?: string }) {
  const content = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon size={16} color="var(--c-text-muted)" />
        <span style={{ fontSize: 15, color: 'var(--c-text)', fontWeight: 500 }}>{label}</span>
      </div>
      <ChevronRight size={16} color="var(--c-text-dim)" />
    </div>
  );
  if (href) {
    return <a href={href} style={{ display: 'block', textDecoration: 'none', borderBottom: '1px solid var(--c-border-lt)' }}>{content}</a>;
  }
  return <div style={{ borderBottom: '1px solid var(--c-border-lt)', cursor: 'default' }}>{content}</div>;
}

// ── Card wrapper ──────────────────────────────────────────────────────────────
function Section({ eyebrow, children }: { eyebrow: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#ffffff',
      borderRadius: 20,
      padding: 20,
      boxShadow: '0 2px 16px rgba(15,26,44,0.06)',
      border: '1px solid var(--c-border)',
    }}>
      <p style={{
        margin: '0 0 14px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: 'var(--c-text-muted)',
      }}>{eyebrow}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ProfileScreen() {
  const { profile, signOut, refresh, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { prefs, update: updatePref } = useNotificationPrefs();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading } = useAvatarUpload(profile?.user_id ?? '');

  // Local avatar URL — updated immediately after upload
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url ?? null);

  // Name edit state
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(profile?.full_name ?? '');
  const [savingName, setSavingName] = useState(false);

  // Sign out
  const [signingOut, setSigningOut] = useState(false);

  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ── Avatar upload ──────────────────────────────────────────────────────────
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await upload(file);
    if (url) {
      setAvatarUrl(url);
      await refresh();
    } else {
      toast.show('Could not upload photo', 'error');
    }
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [upload, refresh, toast]);

  // ── Name save ──────────────────────────────────────────────────────────────
  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || !profile) return;
    setSavingName(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: trimmed })
        .eq('user_id', profile.user_id);
      if (error) { toast.show('Could not save name', 'error'); return; }
      await refresh();
      setEditingName(false);
      toast.show('Name updated', 'success');
    } finally {
      setSavingName(false);
    }
  };

  // ── Sign out ───────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    setSigningOut(true);
    try { await signOut(); } finally { setSigningOut(false); }
  };

  // ── Delete account ─────────────────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const result = await deleteAccount();
      if (!result.ok) {
        toast.show(result.error ?? 'Could not delete account', 'error');
        setShowDeleteModal(false);
        return;
      }
      // signOut is called inside deleteAccount; auth state change navigates to login.
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const displayName = profile?.full_name || formatZambiaPhone(profile?.phone) || '—';
  const initials = profile?.full_name ? getInitials(profile.full_name) : '?';
  const memberSince = profile?.created_at ? formatMemberSince(profile.created_at) : '';
  const kyc = KYC_CONFIG[profile?.kyc_status ?? 'pending'];

  return (
    <div className="app-shell app-shell--with-nav">
      <AppHeader />

      {/* Hidden file input for avatar upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        aria-hidden="true"
        onChange={handleFileChange}
      />

      <main className="scroll-area" style={{ padding: '0 16px 32px' }}>
        <h1 style={{ margin: '16px 0', fontSize: 26, fontWeight: 800, color: 'var(--c-text)' }}>
          Profile
        </h1>

        {/* ── Profile header ──────────────────────────────────────────────── */}
        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          padding: '24px 20px',
          marginBottom: 16,
          boxShadow: '0 2px 16px rgba(15,26,44,0.06)',
          border: '1px solid var(--c-border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}>
          {/* Avatar */}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #2E82D6, #1260B0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              border: '3px solid rgba(255,255,255,0.9)',
              boxShadow: '0 4px 16px rgba(46,130,214,0.25)',
            }}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: 24, fontWeight: 700, color: '#ffffff' }}>{initials}</span>
              )}
            </div>
            {/* Camera button */}
            <button
              type="button"
              aria-label="Upload profile photo"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#ffffff',
                border: '2px solid var(--c-border)',
                boxShadow: '0 2px 8px rgba(15,26,44,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              {uploading
                ? <span style={{ width: 12, height: 12, border: '2px solid var(--c-primary-700)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                : <Camera size={13} color="var(--c-text-sec)" />
              }
            </button>
          </div>

          {/* Name + phone + member since */}
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--c-text)' }}>
              {displayName}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 14, color: 'var(--c-text-muted)' }}>
              {formatZambiaPhone(profile?.phone)}
            </p>
            {memberSince && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--c-text-dim)' }}>
                Member since {memberSince}
              </p>
            )}
          </div>
        </div>

        {/* ── Personal information ────────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <Section eyebrow="Personal Information">
            {/* Full name — editable */}
            <InfoRow
              label="Full name"
              value={
                editingName ? (
                  <input
                    autoFocus
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      border: 'none',
                      borderBottom: '2px solid var(--c-primary-500)',
                      outline: 'none',
                      background: 'transparent',
                      width: '100%',
                      padding: '2px 0',
                    }}
                    aria-label="Edit full name"
                  />
                ) : (
                  <span>{profile?.full_name || '—'}</span>
                )
              }
              action={
                editingName ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      aria-label="Save name"
                      onClick={() => void handleSaveName()}
                      disabled={savingName}
                      style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--c-success-bg)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                      <Check size={14} color="var(--c-success)" />
                    </button>
                    <button
                      type="button"
                      aria-label="Cancel name edit"
                      onClick={() => { setEditingName(false); setNameInput(profile?.full_name ?? ''); }}
                      style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--c-danger-bg)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                      <X size={14} color="var(--c-danger)" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    aria-label="Edit name"
                    onClick={() => { setEditingName(true); setNameInput(profile?.full_name ?? ''); }}
                    style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--c-surface-alt)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                  >
                    <Pencil size={13} color="var(--c-text-muted)" />
                  </button>
                )
              }
            />
            {/* Phone — read-only */}
            <InfoRow
              label="Phone number"
              value={formatZambiaPhone(profile?.phone) ?? '—'}
              action={
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--c-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Lock size={13} color="var(--c-text-dim)" />
                </div>
              }
            />
            {/* NRC */}
            <InfoRow
              label="NRC number"
              value={profile?.nrc || '—'}
            />
          </Section>
        </div>

        {/* ── Verification ────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <Section eyebrow="Verification">
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: kyc.bg,
                borderRadius: 20,
                padding: '5px 12px',
              }}>
                <Shield size={13} color={kyc.color} />
                <span style={{ fontSize: 13, fontWeight: 700, color: kyc.color }}>{kyc.label}</span>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--c-text-muted)' }}>
                {kyc.description}
              </p>
              {profile?.kyc_status === 'rejected' && (
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.kyc)}
                  style={{
                    marginTop: 12,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'var(--c-danger-bg)',
                    color: 'var(--c-danger)',
                    border: 'none',
                    borderRadius: 10,
                    padding: '8px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Re-submit documents
                </button>
              )}
            </div>
          </Section>
        </div>

        {/* ── Notifications ───────────────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <Section eyebrow="Notifications">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: 'var(--c-text)' }}>Loan reminders</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--c-text-muted)' }}>SMS alerts 1 and 3 days before repayment</p>
              </div>
              <Toggle checked={prefs.loanReminders} onChange={(v) => updatePref('loanReminders', v)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: 'var(--c-text)' }}>Promotions</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--c-text-muted)' }}>News about new features and loan tiers</p>
              </div>
              <Toggle checked={prefs.promotions} onChange={(v) => updatePref('promotions', v)} />
            </div>
          </Section>
        </div>

        {/* ── Support ─────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <Section eyebrow="Support">
            <div style={{ margin: '-14px 0' }}>
              <SupportRow icon={ChevronRight} label="Help & FAQ" />
              <SupportRow icon={ChevronRight} label="Contact support" href="tel:+260977000000" />
              <SupportRow icon={ChevronRight} label="Report a problem" href="mailto:support@thandizo.co.zm" />
            </div>
          </Section>
        </div>

        {/* ── Sign out ────────────────────────────────────────────────────── */}
        <Button
          block
          variant="ghost"
          loading={signingOut}
          onClick={() => void handleSignOut()}
          style={{ marginBottom: 16, color: 'var(--c-text-sec)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <LogOut size={16} />
          Sign out
        </Button>

        {/* ── Danger zone ─────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => { setDeleteConfirm(''); setShowDeleteModal(true); }}
            style={{
              background: 'none',
              border: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--c-danger)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '8px 16px',
            }}
          >
            <Trash2 size={15} />
            Delete account
          </button>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--c-text-dim)', margin: 0 }}>
          Thandizo v{APP_VERSION} ·{' '}
          <a href="#" style={{ color: 'var(--c-text-dim)' }}>Terms</a>{' '}·{' '}
          <a href="#" style={{ color: 'var(--c-text-dim)' }}>Privacy</a>
        </p>
      </main>

      <NavBar />

      {/* ── Delete account modal ─────────────────────────────────────────── */}
      {showDeleteModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          }}
        >
          {/* Backdrop */}
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(15,26,44,0.5)', backdropFilter: 'blur(2px)' }}
            onClick={() => setShowDeleteModal(false)}
          />

          {/* Sheet */}
          <div style={{
            position: 'relative',
            background: '#ffffff',
            borderRadius: '24px 24px 0 0',
            padding: '28px 24px 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}>
            {/* Icon + title */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--c-danger-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={24} color="var(--c-danger)" />
              </div>
              <h2 id="delete-modal-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--c-text)', textAlign: 'center' }}>
                Delete your account
              </h2>
            </div>

            {/* Explanation */}
            <p style={{ margin: 0, fontSize: 14, color: 'var(--c-text-sec)', lineHeight: 1.6, textAlign: 'center' }}>
              This will permanently erase your personal details and login access.
              Loan and payment records are retained for <strong>7 years</strong> as required by
              Zambian financial regulations.
            </p>

            {/* Confirmation input */}
            <div>
              <label
                htmlFor="delete-confirm-input"
                style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-sec)', marginBottom: 8 }}
              >
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                id="delete-confirm-input"
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: `1.5px solid ${deleteConfirm === 'DELETE' ? 'var(--c-danger)' : 'var(--c-border)'}`,
                  background: 'var(--c-surface-alt)',
                  fontSize: 15,
                  letterSpacing: 2,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Actions */}
            <Button
              block
              variant="danger"
              loading={deleting}
              disabled={deleteConfirm !== 'DELETE'}
              onClick={() => void handleDeleteAccount()}
            >
              Delete account
            </Button>
            <Button
              block
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
